<template>
  <div class="main-main" @click="closeAbout">
    <div
      class="static-bg"
      :class="{ movingBg: audioPlaying }"
      :style="backgroundStyle"
    ></div>
    <div class="main-hero section-container">
      <!-- About modal is now handled globally in App.vue -->
      <div class="main-max-width max-width">
        <now-playing
          v-if="currentTrack"
          @audioPlaying="changeAudioPlayingState"
          :artist="currentTrack.artist"
          :title="currentTrack.title"
          :album="currentTrack.album"
          :year="currentTrack.year"
          :discogsUrl="currentTrack.discogs_url"
          :imageUrl="currentTrack.image_url"
          :favorited="currentTrack.favorited"
          @toggle-favorite="toggleFavoriteCurrentTrack"
          @open-signup="$emit('open-signup')"
        ></now-playing>
        <div v-else class="loading">Loading current track...</div>
        <div class="main-last-played">
          <div class="header-pasttrack">
            <span class="main-track">LAST PLAYED</span>
          </div>
          <last-played
            v-if="lastTracks && lastTracks.length"
            :lastTracks="lastTracks"
            @toggle-favorite="toggleFavoriteTrack"
            @open-signup="$emit('open-signup')"
          ></last-played>
          <div v-else class="loading">Loading history...</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import api from '../api';
import { auth } from '../firebase';
import { onAuthStateChanged } from 'firebase/auth';
import NowPlaying from "./now-playing";
import LastPlayed from "./last-played";
import About from "./about";

export default {
  name: "Main",
  props: {
    showAbout: {
      type: Boolean,
      default: false,
    },
  },
  components: {
    NowPlaying,
    LastPlayed,
    About,
  },

  data() {
    return {
      currentTrack: null,
      lastTracks: [],
      isLandscape: true,
      audioPlaying: false,
      user: null,
      screenWidth: window.innerWidth,
      trackInterval: null,
      // Session tracking
      sessionId: null,
      sessionStartTime: null,
      listeningStartTime: null,
      totalListeningTime: 0,
      // Favorites management
      favorites: [],
      lastTrackId: null,
      favoritesLoaded: false
    };
  },
  created() {
    onAuthStateChanged(auth, user => {
      this.user = user;
      if (user) {
        // Load favorites immediately when user logs in
        this.loadFavorites();
        // Load track list immediately when user logs in
        this.fetchNuanceTracks();
        // Start session (but don't block on it)
        this.startSession().catch(() => {
          // Session API might not exist, that's okay
        });
      } else {
        this.endSession().catch(() => {
          // Session API might not exist, that's okay
        });
        // Clear favorites when user logs out
        this.favorites = [];
        this.favoritesLoaded = false;
      }
    });
  },
  methods: {
    async fetchNuanceTracks() {
      // Fetch track list only
      const response = await api.get('/track_list');
        const current = response.data.shift().track;
      let lastTracks = response.data
          .map((e) => e.track)
          .filter(
            (track) =>
              track &&
              track.artist &&
              track.title &&
              !(
                current.artist &&
                current.title &&
                track.artist.trim().toLowerCase() === current.artist.trim().toLowerCase() &&
                track.title.trim().toLowerCase() === current.title.trim().toLowerCase()
              )
          );
      
      // Check if track changed (for smart favorites loading)
      const trackChanged = this.lastTrackId !== current.id;
      this.lastTrackId = current.id;
      
      // Load favorites if track changed and user is logged in
      if (this.user && trackChanged && !this.favoritesLoaded) {
        await this.loadFavorites();
      }
      
      // Default: not favorited
      current.favorited = false;
      lastTracks = lastTracks.map(track => { track.favorited = false; return track; });
      
      // Mark tracks as favorited if user is logged in and favorites are loaded
      if (this.user && this.favoritesLoaded) {
        this.markTracksAsFavorited(current, lastTracks);
      }
      
        this.currentTrack = current;
        this.lastTracks = lastTracks;
    },
    
    async loadFavorites() {
      if (!this.user) return;
      
      try {
        const favRes = await api.get(`/favorites/${this.user.uid}`);
        this.favorites = favRes.data || [];
        this.favoritesLoaded = true;
        console.log('🎵 Favorites loaded:', this.favorites.length, 'tracks');
      } catch (error) {
        console.error('Failed to load favorites:', error);
        this.favorites = [];
        this.favoritesLoaded = false;
      }
    },
    
    markTracksAsFavorited(current, lastTracks) {
      // Helper to check if a track is in favorites
      const isFavorited = (track) => {
        const found = this.favorites.some(fav => {
          // Check by ID first (most reliable)
          if (fav.id && track.id && fav.id === track.id) {
            return true;
          }
          // Check by artist + title (fallback)
          if (fav.artist && fav.title && track.artist && track.title) {
            return fav.artist.trim().toLowerCase() === track.artist.trim().toLowerCase() &&
                   fav.title.trim().toLowerCase() === track.title.trim().toLowerCase();
          }
          return false;
        });
        
        return found;
      };
      
      // Mark current track
      current.favorited = isFavorited(current);
      
      // Mark last tracks
      lastTracks.forEach(track => {
        track.favorited = isFavorited(track);
        });
    },
    
    handleResize() {
      this.isLandscape = window.innerWidth > window.innerHeight;
      this.screenWidth = window.innerWidth;
    },
    closeAbout() {
      if (this.showAbout) {
        this.$emit("close-about", false);
      }
    },
    // Session tracking methods
    async startSession() {
      if (!this.user) return;
      
      try {
        const response = await api.post(`/sessions/${this.user.uid}/start`);
        this.sessionId = response.data.session_id;
        this.sessionStartTime = new Date();
        this.totalListeningTime = 0;
        
        // Handle page unload to save session
        window.addEventListener('beforeunload', this.handlePageUnload);
      } catch (error) {
        // Session API might not exist, that's okay - don't log error
      }
    },
    
    async endSession() {
      if (!this.user || !this.sessionId) return;
      
      try {
        // Calculate total listening time
        if (this.listeningStartTime && this.audioPlaying) {
          const now = new Date();
          this.totalListeningTime += (now - this.listeningStartTime) / 1000;
        }
        
        await api.post(`/sessions/${this.user.uid}/end`, {
          session_id: this.sessionId,
          listening_duration: this.totalListeningTime
        });
        
        this.sessionId = null;
        this.sessionStartTime = null;
        this.listeningStartTime = null;
        this.totalListeningTime = 0;
        
        window.removeEventListener('beforeunload', this.handlePageUnload);
      } catch (error) {
        // Session API might not exist, that's okay - don't log error
      }
    },
    
    handlePageUnload() {
      this.endSession();
    },
    

    
    changeAudioPlayingState(isPlaying) {
      this.audioPlaying = isPlaying;
      
      // Track listening time
      if (isPlaying) {
        this.listeningStartTime = new Date();
      } else if (this.listeningStartTime) {
        const now = new Date();
        this.totalListeningTime += (now - this.listeningStartTime) / 1000;
        this.listeningStartTime = null;
      }
    },
    async toggleFavoriteCurrentTrack() {
      if (!this.user || !this.currentTrack) return;
      
      try {
        const isFavorited = this.currentTrack.favorited;
        
        if (isFavorited) {
          // Remove from favorites
          await api.delete(`/favorites/${this.user.uid}`, { 
            data: { 
              id: this.currentTrack.id, 
              artist: this.currentTrack.artist, 
              title: this.currentTrack.title 
            } 
          });
        } else {
          // Add to favorites
          await api.post(`/favorites/${this.user.uid}`, {
            id: this.currentTrack.id,
            artist: this.currentTrack.artist,
            title: this.currentTrack.title,
            album: this.currentTrack.album,
            year: this.currentTrack.year,
            image_url: this.currentTrack.image_url,
            discogs_url: this.currentTrack.discogs_url
          });
        }
        
        // Update local state only after successful API call
        this.$set(this.currentTrack, 'favorited', !isFavorited);
        
        // Update favorites array
        if (isFavorited) {
          // Remove from favorites array
          this.favorites = this.favorites.filter(fav => 
            !((fav.id && this.currentTrack.id && fav.id === this.currentTrack.id) ||
              (fav.artist === this.currentTrack.artist && fav.title === this.currentTrack.title))
          );
        } else {
          // Add to favorites array
          this.favorites.unshift({
            id: this.currentTrack.id,
            artist: this.currentTrack.artist,
            title: this.currentTrack.title,
            album: this.currentTrack.album,
            year: this.currentTrack.year,
            image_url: this.currentTrack.image_url,
            discogs_url: this.currentTrack.discogs_url
          });
        }
        
        // Emit event for other components
        if (this.$eventBus) {
          this.$eventBus.$emit('favorites-updated');
        }
        
      } catch (error) {
        console.error('Failed to toggle favorite:', error);
        // Don't update local state if API call failed
      }
    },
    
    async toggleFavoriteTrack(idx) {
      if (!this.user || !this.lastTracks[idx]) return;
      
      try {
        const track = this.lastTracks[idx];
        const isFavorited = track.favorited;
        
        if (isFavorited) {
          // Remove from favorites
          await api.delete(`/favorites/${this.user.uid}`, { 
            data: { 
              id: track.id, 
              artist: track.artist, 
              title: track.title 
            } 
          });
        } else {
          // Add to favorites
          await api.post(`/favorites/${this.user.uid}`, {
            id: track.id,
            artist: track.artist,
            title: track.title,
            album: track.album,
            year: track.year,
            image_url: track.image_url,
            discogs_url: track.discogs_url
          });
        }
        
        // Update local state only after successful API call
        this.$set(this.lastTracks[idx], 'favorited', !isFavorited);
        
        // Update favorites array
        if (isFavorited) {
          // Remove from favorites array
          this.favorites = this.favorites.filter(fav => 
            !((fav.id && track.id && fav.id === track.id) ||
              (fav.artist === track.artist && fav.title === track.title))
          );
        } else {
          // Add to favorites array
          this.favorites.unshift({
            id: track.id,
            artist: track.artist,
            title: track.title,
            album: track.album,
            year: track.year,
            image_url: track.image_url,
            discogs_url: track.discogs_url
          });
        }
        
        // Emit event for other components
        if (this.$eventBus) {
          this.$eventBus.$emit('favorites-updated');
        }
        
      } catch (error) {
        console.error('Failed to toggle favorite:', error);
        // Don't update local state if API call failed
      }
    },
  },
  computed: {
    backgroundStyle() {
      const width = this.screenWidth;
      // Use new mobile backgrounds for mobile/tablet
      if (width <= 991) {
        return `background-image: url('/playground_assets/nuance-background-vertical-1500w-mobile.jpg'),linear-gradient(90deg, rgb(189, 195, 199) 0.00%,rgb(0, 0, 0) 100.00%)`;
      }
      return `background-image: url('/playground_assets/nuance-background-test-4-1500h.jpg'),linear-gradient(90deg, rgb(189, 195, 199) 0.00%,rgb(0, 0, 0) 100.00%)`;
    },
  },
  mounted() {
    // Load tracks immediately for all users
    this.fetchNuanceTracks();
    
    // Set up polling interval
    const isMobile = /Android|iPhone|iPad/.test(navigator.userAgent);
    this.trackInterval = setInterval(this.fetchNuanceTracks, isMobile ? 10000 : 5000);
    
    window.addEventListener("orientationchange", this.handleResize);
    window.addEventListener("resize", this.handleResize);
  },
  beforeDestroy() {
    // Clean up interval
    if (this.trackInterval) {
      clearInterval(this.trackInterval);
      this.trackInterval = null;
    }
    
    // Remove event listeners
    window.removeEventListener("orientationchange", this.handleResize);
    window.removeEventListener("resize", this.handleResize);
  },
};
</script>

<style scoped>
.about-box {
  max-width: 100vh;
  margin: 10px;
  height: 100%;
}

.main-main {
  width: 100%;
  /* display: flex; */
  flex: 1;
  position: relative;
  border-color: #000000;
  border-width: 0.5rem;
}

.static-bg {
  width: 100%;
  height: 100%;
  position: absolute;
  background-size: cover;
  z-index: -1;
  /* GPU acceleration for better performance */
  transform: translateZ(0);
  will-change: transform;
}

@media (min-width: 991px) {
  .main-main {
    overflow: hidden;
  }

  .movingBg {
    animation: zoomInOut 30s infinite; /* Slow zoom in/out over 30 seconds */
    animation-timing-function: ease-in-out; /* Smooth easing */
  }
}

/* Disable animations on mobile to prevent overheating */
@media (max-width: 990px) {
  .movingBg {
    animation: none !important;
  }
}

.main-hero {
  width: 100%;
  height: 100%;
  position: relative;
  align-items: center;
  padding-top: 0px;
  padding-bottom: 0px;
  justify-content: center;
  background-color: transparent;
}
.section-container {
  width: 100%;
  display: flex;
  overflow: hidden;
  align-items: center;
  flex-direction: column;
}
.main-max-width {
  width: 55%;
  align-items: center;
  flex-direction: row;
  /* justify-content: center; */
}
.main-last-played {
  width: 100%;
  position: relative;
}
.header-pasttrack {
  top: 4px;
  left: 0px;
  position: absolute;
  transform: translateY(-100%);
  flex-direction: column;
  justify-content: flex-start;
}
.main-track {
  color: #ff7676;
  font-size: var(--played) !important;
  transform: translateY(0.2%);
  font-style: normal;
  font-family: Syne;
  line-height: 1;
  padding: 2px 4px 2px 4px;
  background-color: #ffffff;
}
.main-event {
  display: none;
  align-items: center;
}
.main-text {
  color: #000000;
  font-style: normal;
  font-family: Syne;
  font-weight: 400;
  text-decoration: underline none;
}
.main-text2 {
  font-weight: 700;
}
.main-socials {
  display: none;
}
.main-root-class-name {
  height: 100%;
}
@media (max-width: 1600px) {
  .main-max-width {
    width: 65%;
  }
  .main-root-class-name {
    height: 100%;
  }
}
@media (max-width: 1200px) {
  .main-main {
    border-width: 0.8vw;
  }
  .main-hero {
    padding-top: 0px;
    padding-bottom: 0px;
  }
  .main-max-width {
    width: 70%;
  }
  .main-last-played {
    width: 100%;
  }
  .main-track {
    transform: translateY(20%);
    line-height: 1;
    padding: 0.2vw 6px;
  }
  .main-text {
    font-size: 0.9vw;
  }
  .main-root-class-name {
    height: 100%;
  }
}

@media (max-width: 991px) {
  .main-main {
    border-width: 0.8vw;
    position: static !important;
  }
  .main-hero {
    padding-top: 40px;
    padding-left: 100;
    padding-right: 100;
    justify-content: unset;
  }
  .main-max-width {
    width: 90%;
    height: auto;
    flex-direction: column;
    margin-bottom: 4vw;
  }
  .main-last-played {
    margin-top: 60px;
  }
  .static-bg {
    background-size: cover !important;
    background-position: center center !important;
    background-repeat: no-repeat !important;
    min-height: 100vh;
    height: 100vh;
    top: 0;
    bottom: 0;
  }
  .main-hero {
    margin-top: 0vw;
  }
}
@media (min-width: 992px) {
  .main-main {
    background-image: url('/playground_assets/nuance-background-test-4-1500h.jpg'),linear-gradient(90deg, rgb(189, 195, 199) 0.00%,rgb(0, 0, 0) 100.00%);
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    background-color: #e0f7fa;
  }
}
@media (max-width: 767px) {
  .static-bg {
    position: fixed !important;
    width: 100vw !important;
    height: 100vh !important;
    top: 0 !important;
    left: 0 !important;
    right: 0 !important;
    bottom: 0 !important;
    background-attachment: fixed !important;
    background-size: cover !important;
    background-position: center center !important;
    background-repeat: no-repeat !important;
    z-index: -1 !important;
  }
  .main-main {
    position: relative;
    z-index: 1;
    background: transparent;
  }
  .main-hero {
    width: 100%;
    align-self: flex-start;
    padding: 40px 0px 0px 0px;
    margin-top: 12vw;
  }

  .main-max-width {
    widows: 100%;
  }
  .main-event {
    display: flex;
    background: white;
    padding-top: 1vw;
    padding-left: 2vw;
    margin-bottom: 3vw;
    padding-right: 2vw;
    padding-bottom: 1vw;
  }
  .main-text {
    font-size: 3vw;
  }
  .main-socials {
    display: flex;
    background: white;
    padding-top: 1vw;
    padding-left: 1vw;
    margin-bottom: 10vw;
    padding-right: 1vw;
    padding-bottom: 1vw;
  }
  .main-icon {
    width: 4vw;
    margin-right: 1.3vw;
  }
  .main-icon2 {
    width: 3.5vw;
    margin-right: 1.5vw;
  }
  .main-icon4 {
    width: 4vw;
  }
  .header-pasttrack {
    top: 4px;
    left: 0px;
    position: absolute;
    justify-content: center;
  }
}
@media (max-width: 479px) {
  .main-hero {
    padding-left: 0;
    padding-right: 0px;
  }
  .main-track {
    transform: translateY(1, 1);
    padding: 2px 4px 2px 4px;
  }
  .main-socials {
    background: white;
  }
}
.loading {
  color: #888;
  text-align: center;
  margin: 2em 0;
  font-size: 1.2em;
}

/* Mobile styles */
@media (max-width: 768px) {
  .main-main {
    background-image: url('/playground_assets/nuance-background-vertical-1500w-mobile.jpg'),linear-gradient(90deg, rgb(189, 195, 199) 0.00%,rgb(0, 0, 0) 100.00%);
    background-size: cover;
    background-position: center center;
    background-repeat: no-repeat;
    padding-bottom: 80px; /* Space for fixed footer */
  }
}
</style>
